import type { Location } from 'react-router'
import type { ReactElement } from 'react'
import type { Store } from 'redux'
import { fromJS } from 'immutable'
import { useLocation, useNavigate } from 'react-router'
import { useState } from 'react'
import { useStore } from 'react-redux'

import type { ProductDataType } from '../Workspace/plugins/category/CategoryPlugin'
import {
  buildFilterArray,
  resetAllCategoryFilters,
  selectCategorySearchFacetValue,
  setCategorySearchFacetRange,
  setProductsForCategoryAsync,
} from '../../../store/actions'
import { generateCategoryUrl } from '../../../urlGenerators'
import { getFilters } from './FacetedSearchToolbarTop'
import { pagingDefaults, sortingDefaults } from '../../../utils/pageAndSort'
import { withQueryAndScope } from '../../../urlGeneratorsPlain'
import SearchFilterButton from './SearchFilterButton'
import SearchFilterOverlay from './SearchFilterOverlay'
import SearchFilters from './SearchFilters'
import ToolbarTop from '../ToolbarTop'
import buildURL from '../../../../utils/buildURL'

function getCategorySearchQuery(store: Store<State>, location: Location, slug: string, categoryId: string): string {
  const locationQuery = new URLSearchParams(location.search)

  const productData = store.getState().getIn(['categoryProductData', categoryId])
  const facets: Core.Facets | undefined = productData?.facets
  const imageSize = store.getState().getIn(['categories', categoryId, 'settings', 'imageSize'], 'M')
  const pageSize = imageSize === 'S' ? 15 : 12

  const searchFilters = buildFilterArray(locationQuery)
  const searchParams = new URLSearchParams()
  const searchParamsObj = getFilters(facets)

  Object.entries(searchParamsObj).forEach(([key, param]) => {
    if (Array.isArray(param)) {
      param.forEach((value) => searchParams.append(key, value))
    } else {
      searchParams.append(key, param)
    }
  })

  const sort = locationQuery.get('sort')
  if (sort) searchParams.append('sort', sort)

  const categorySlug = fromJS({
    slug,
  })

  store.dispatch<any>(
    setProductsForCategoryAsync(
      categoryId,
      {
        page: pagingDefaults.page,
        resultsPerPage: pageSize,
        sort: sort ?? sortingDefaults.sort,
      },
      searchFilters,
    ),
  )

  return buildURL(withQueryAndScope(generateCategoryUrl(categorySlug), location) as string, searchParams)
}

function scrollToMarker(marker: string) {
  const target = document.querySelector(`[data-marker="${marker}"]`)

  if (target) target.scrollIntoView({ behavior: 'smooth' })
}

type Props = {
  category: Core.Page & {
    categoryId: string
    objectId: number
    facetedSearchCategoryStartDepth: number
    facetedSearchShowFacetsOnCategory: boolean
  }
  categoryDepth: number
  productData: ProductDataType
  isBusy: boolean
  isEditor?: boolean
  onScrollIntoView: VoidFunction
} & TranslateProps

export default function CategorySearch({
  category,
  categoryDepth,
  productData,
  isBusy,
  isEditor = false,
  t,
  onScrollIntoView,
}: Readonly<Props>): ReactElement | null {
  const [isOpen, setIsOpen] = useState(false)
  const store = useStore<State>()
  const navigate = useNavigate()
  const location = useLocation()

  const handleSelectionFilterChange = (facetId: string, value: Core.FacetSelectionValue) => {
    store.dispatch(selectCategorySearchFacetValue(facetId, value.value, category.categoryId, !value.selected))
    navigate(getCategorySearchQuery(store, location, category.slug, category.categoryId), { replace: true })
    scrollToMarker('product-section')
  }

  const handleRangeFilterChange = (facetId: string, range: { min: number; max: number }) => {
    store.dispatch(setCategorySearchFacetRange(facetId, category.categoryId, range))
    navigate(getCategorySearchQuery(store, location, category.slug, category.categoryId), { replace: true })
    scrollToMarker('product-section')
  }

  const handleResetAllCategoryFilters = () => {
    store.dispatch(resetAllCategoryFilters(category.categoryId))
    navigate(getCategorySearchQuery(store, location, category.slug, category.categoryId), { replace: true })
  }

  const hasFacets = Object.keys(productData.facets ?? {}).length !== 0

  if (!category.facetedSearchShowFacetsOnCategory) return null
  if (!hasFacets && !isEditor) return null
  if (categoryDepth < category.facetedSearchCategoryStartDepth) return null

  return (
    <div className="faceted-search-toolbar-top-wrapper">
      <ToolbarTop className="faceted-search-toolbar-top" onScrollIntoView={onScrollIntoView}>
        <div className="search-configuration">
          <SearchFilterButton onClick={() => setIsOpen(true)}>
            {t('components.facetedSearch.filterButton.label')}
          </SearchFilterButton>
        </div>
        <SearchFilters
          facets={productData.facets}
          onSelectionFilterChange={handleSelectionFilterChange}
          onRangeFilterChange={handleRangeFilterChange}
        />
        <SearchFilterOverlay
          facets={productData.facets}
          totalNumberOfProducts={productData.totalNumberOfProducts ?? 0}
          isBusy={isBusy}
          isOpen={isOpen}
          onSelectionFacetChange={(facet, value) => {
            handleSelectionFilterChange(facet.id, value)
          }}
          onRangeFacetChange={(facet, range) => {
            handleRangeFilterChange(facet.id, range)
          }}
          onResetAllFilters={handleResetAllCategoryFilters}
          onRequestClose={() => setIsOpen(false)}
          t={t}
        />
      </ToolbarTop>
    </div>
  )
}
